import { useState, useCallback, useEffect } from 'react';
import {
  Modal, Stepper, Button, Group, Textarea, Select, TextInput,
  Text, Alert, Stack, Loader, Center, Image,
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useLocation } from 'react-router-dom';
import { AnnotationCanvas } from './AnnotationCanvas';
import { useCreateIssue } from '../../api/hooks/useIssues';

interface IssueReporterModalProps {
  opened: boolean;
  onClose: () => void;
  /** Pre-captured screenshot blob (captured before modal opened) */
  initialScreenshot: Blob | null;
}

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low — cosmetic or minor' },
  { value: 'medium', label: 'Medium — functional but has workaround' },
  { value: 'high', label: 'High — broken functionality' },
  { value: 'critical', label: 'Critical — blocking, data loss risk' },
];

export function IssueReporterModal({ opened, onClose, initialScreenshot }: IssueReporterModalProps) {
  // Start at annotate step (step 0) since screenshot is pre-captured
  const [step, setStep] = useState(0);
  const [originalBlob, setOriginalBlob] = useState<Blob | null>(null);
  const [annotatedBlob, setAnnotatedBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<string>('medium');
  const [affectedArea, setAffectedArea] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { pathname } = useLocation();
  const createIssue = useCreateIssue();

  // When modal opens with a pre-captured screenshot, set it up
  useEffect(() => {
    if (opened && initialScreenshot) {
      setOriginalBlob(initialScreenshot);
      setAnnotatedBlob(initialScreenshot);
      const url = URL.createObjectURL(initialScreenshot);
      setPreviewUrl(url);
      setStep(0); // annotation step
      return () => URL.revokeObjectURL(url);
    }
  }, [opened, initialScreenshot]);

  const handleAnnotationExport = useCallback((blob: Blob) => {
    setAnnotatedBlob(blob);
  }, []);

  const handleSubmit = async () => {
    if (!description.trim()) {
      setSubmitError('Please describe the issue');
      return;
    }

    setSubmitError(null);

    const formData = new FormData();
    formData.append('summary', description.trim());
    formData.append('severity', severity);
    formData.append('affectedArea', affectedArea || 'general');
    formData.append('affectedRoute', pathname);
    formData.append('browserInfo', navigator.userAgent);

    if (originalBlob) {
      formData.append('original', originalBlob, 'original.png');
    }
    if (annotatedBlob) {
      formData.append('annotated', annotatedBlob, 'annotated.png');
    }

    try {
      await createIssue.mutateAsync(formData);
      handleClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit issue');
    }
  };

  const handleClose = () => {
    setStep(0);
    setOriginalBlob(null);
    setAnnotatedBlob(null);
    setDescription('');
    setSeverity('medium');
    setAffectedArea('');
    setSubmitError(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Report an Issue"
      size="xl"
      centered
    >
      <Stepper active={step} onStepClick={setStep}>
        {/* Step 0: Annotate the pre-captured screenshot */}
        <Stepper.Step label="Annotate" description="Mark up the issue">
          <Stack gap="md" py="md">
            {originalBlob ? (
              <>
                <Text size="sm" c="dimmed">
                  Use the drawing tools to highlight the problem. Draw arrows, boxes, or add text.
                </Text>
                <AnnotationCanvas
                  imageBlob={originalBlob}
                  onExport={handleAnnotationExport}
                />
              </>
            ) : (
              <Center h={200}>
                <Stack align="center" gap="xs">
                  <Text c="dimmed">No screenshot captured. You can still describe the issue.</Text>
                </Stack>
              </Center>
            )}
            <Group justify="flex-end">
              <Button onClick={() => setStep(1)}>Next: Describe</Button>
            </Group>
          </Stack>
        </Stepper.Step>

        {/* Step 1: Describe & Submit */}
        <Stepper.Step label="Describe" description="Details & submit">
          <Stack gap="md" py="md">
            {previewUrl && (
              <Image
                src={previewUrl}
                alt="Captured screenshot"
                radius="sm"
                style={{ maxHeight: 200, objectFit: 'contain' }}
              />
            )}

            <Textarea
              label="What's the issue?"
              placeholder="Describe what's wrong, what you expected, and any steps to reproduce..."
              minRows={4}
              maxRows={8}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              required
            />
            <Select
              label="Severity"
              data={SEVERITY_OPTIONS}
              value={severity}
              onChange={(v) => setSeverity(v || 'medium')}
            />
            <TextInput
              label="Affected area"
              placeholder="e.g., Dashboard, Chat, Contacts..."
              value={affectedArea}
              onChange={(e) => setAffectedArea(e.currentTarget.value)}
            />
            <Text size="xs" c="dimmed">
              Page: {pathname}
            </Text>

            {submitError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {submitError}
              </Alert>
            )}

            <Group justify="flex-end">
              <Button variant="light" onClick={() => setStep(0)}>Back</Button>
              <Button
                leftSection={createIssue.isPending ? <Loader size={16} /> : <IconCheck size={16} />}
                onClick={handleSubmit}
                loading={createIssue.isPending}
                color="green"
              >
                Submit Issue
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>
      </Stepper>
    </Modal>
  );
}
